import { type FC, type ReactNode } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  Link as MuiLink,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DialogHeader } from "@/shared/components";
import { PagePaths } from "@/partner/shared/constants/routes";
import { PAYMENT_PROVIDER_LABEL_KEY } from "@/partner/shared/utils/paymentProvider";
import { interpolationFormat } from "@/partner/shared/lib";
import {
  PaymentBreakdown,
  PaymentRefundsTable,
} from "@/partner/features/purchases";
import {
  type PaymentsListQueryType,
  PaymentStatusType,
} from "@/types/graphql/partner.generated";
import {
  PAYMENT_STATUS_COLOR,
  PAYMENT_STATUS_KEY,
} from "../model/paymentStatus";
import { RefundButton } from "./RefundButton";

type PaymentRow = PaymentsListQueryType["payments"]["nodes"][number];

interface PaymentDetailsDialogProps {
  open: boolean;
  payment: PaymentRow | null;
  onClose: () => void;
}

// Тот же лейбл-значение блок, что в диалоге покупки.
const Field: FC<{ label: string; children: ReactNode }> = ({
  label,
  children,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" component="div">
      {children}
    </Typography>
  </Box>
);

/**
 * Детали платежа: те же блоки, что в диалоге покупки (раскладка сумм и
 * возвраты), плюс поля самого платежа. Референс раскладки —
 * whitelabelAdmin/components/PaymentDetails/PaymentDetails.js.
 */
export const PaymentDetailsDialog: FC<PaymentDetailsDialogProps> = ({
  open,
  payment,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const title = payment?.product?.title ?? t("payments.details.title");
  const emptyValue = t("common.emptyDash");
  const refunds = payment?.refunds ?? [];

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      scroll="body"
      open={open}
      onClose={onClose}
    >
      <Box
        sx={{ position: "sticky", top: 0, zIndex: 1 }}
        bgcolor="background.paper"
      >
        <DialogHeader
          title={title}
          onClose={onClose}
          actions={
            payment && payment.status === PaymentStatusType.SucceededType ? (
              <RefundButton
                paymentId={payment.id}
                provider={payment.provider}
                defaultAmount={payment.amount.value}
              />
            ) : undefined
          }
        />
      </Box>
      <DialogContent sx={{ p: 1 }}>
        <Paper sx={{ p: 2 }}>
          {payment && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("payments.details.productLabel")}>
                  {payment.product ? (
                    <MuiLink
                      component={RouterLink}
                      target="_blank"
                      to={`${PagePaths.products}?tab=edit&id=${payment.product.id}`}
                    >
                      {payment.product.title}
                    </MuiLink>
                  ) : (
                    emptyValue
                  )}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("payments.details.buyerLabel")}>
                  {payment.buyerEmail ? (
                    <MuiLink
                      component={RouterLink}
                      color="secondary"
                      target="_blank"
                      to={`${PagePaths.users}?tab=details&id=${payment.userId}`}
                    >
                      {payment.buyerEmail}
                    </MuiLink>
                  ) : (
                    emptyValue
                  )}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("payments.details.providerLabel")}>
                  {t(PAYMENT_PROVIDER_LABEL_KEY[payment.provider])}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("payments.details.statusLabel")}>
                  <Chip
                    size="small"
                    color={PAYMENT_STATUS_COLOR[payment.status]}
                    label={t(
                      `payments.status.${PAYMENT_STATUS_KEY[payment.status]}`
                    )}
                  />
                </Field>
              </Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("payments.details.breakdownTitle")}
                </Typography>
                <PaymentBreakdown payment={payment} />
              </Grid>
              {refunds.length > 0 && (
                <Grid size={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("payments.details.refundsTitle")}
                  </Typography>
                  <PaymentRefundsTable refunds={refunds} />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("payments.details.periodLabel")}>
                  {t("purchases.list.periodDays", { count: payment.period })}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("payments.details.createdAtLabel")}>
                  {interpolationFormat(
                    payment.createdAt,
                    "dateTime",
                    i18n.language
                  )}
                </Field>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Field label={t("payments.details.recurrentLabel")}>
                  {t(payment.recurrent ? "common.yes" : "common.no")}
                </Field>
              </Grid>
            </Grid>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};
